// src/components/onboarding/MirrorSession.tsx
// The Mirror Session - Mystara's first deep conversation with a new user
// This builds the foundation of their Soul File

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, ArrowRight, Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import type { ManifestationCategory } from '../../types/mystara';

interface MirrorSessionProps {
  onComplete: () => void;
}

type SessionStage = 
  | 'welcome'
  | 'name'
  | 'what-brought-you'
  | 'desires'
  | 'feeling-underneath'
  | 'current-state'
  | 'communication-style'
  | 'closing';

interface StageConfig {
  stage: SessionStage;
  mystaraMessage: string | ((context: SessionContext) => string);
  inputType: 'text' | 'choice' | 'textarea' | 'multi-choice';
  choices?: string[] | { value: string; label: string }[];
  placeholder?: string;
  skipable?: boolean;
  onResponse?: (response: string, context: SessionContext) => Partial<SessionContext>;
}

interface SessionContext {
  name: string;
  whatBroughtYou: string;
  primaryDesire: string;
  desireCategory: ManifestationCategory | null;
  feelingUnderneath: string;
  currentState: string;
  communicationStyle: 'gentle' | 'direct' | 'balanced';
}

const STAGES: StageConfig[] = [
  {
    stage: 'welcome',
    mystaraMessage: "Hi there ✨\n\nI'm Mystara, and I'm genuinely happy you're here.\n\nBefore we dive into tools and features, I'd love to actually get to know you. This conversation will help me understand who you are and what you're hoping to create — so I can be genuinely helpful, not just generic.\n\nSound good?",
    inputType: 'choice',
    choices: [
      { value: 'yes', label: "Yes, let's do this" },
      { value: 'sure', label: "Sure, I'm curious" }
    ]
  },
  {
    stage: 'name',
    mystaraMessage: "Perfect. Let's start simple — what would you like me to call you?\n\nThis can be your name, a nickname, whatever feels right.",
    inputType: 'text',
    placeholder: "What should I call you?",
    onResponse: (response) => ({ name: response.trim() })
  },
  {
    stage: 'what-brought-you',
    mystaraMessage: (ctx) => `Nice to meet you, ${ctx.name} 💫\n\nI'm curious — what brought you here? What made you want to explore something like Mystara?\n\nThere's no right answer. I just want to understand where you're at.`,
    inputType: 'textarea',
    placeholder: "What's on your mind?",
    onResponse: (response) => ({ whatBroughtYou: response })
  },
  {
    stage: 'desires',
    mystaraMessage: (ctx) => `Thank you for sharing that.\n\nIf you could wave a magic wand and change or create one thing in your life right now, what would it be?\n\nThis doesn't have to be "realistic" — I want to know what you actually want.`,
    inputType: 'textarea',
    placeholder: "What are you calling in?",
    onResponse: (response) => ({ primaryDesire: response })
  },
  {
    stage: 'feeling-underneath',
    mystaraMessage: (ctx) => `"${ctx.primaryDesire.slice(0, 100)}${ctx.primaryDesire.length > 100 ? '...' : ''}"\n\nI hear that. Now here's the deeper question:\n\nWhat's the *feeling* you think having this would give you?\n\nFor example — is it freedom? Safety? Joy? Being seen? Peace? Something else?`,
    inputType: 'textarea',
    placeholder: "What feeling are you really seeking?",
    onResponse: (response) => ({ feelingUnderneath: response })
  },
  {
    stage: 'current-state',
    mystaraMessage: (ctx) => `${ctx.feelingUnderneath}. That's beautiful, and worth pursuing.\n\nOne more question about where you are right now:\n\nHow would you describe your current relationship with that feeling? Do you experience it sometimes, rarely, or almost never?`,
    inputType: 'choice',
    choices: [
      { value: 'sometimes', label: "Sometimes — it comes and goes" },
      { value: 'rarely', label: "Rarely — I catch glimpses but it's fleeting" },
      { value: 'almost-never', label: "Almost never — that's why I'm here" }
    ],
    onResponse: (response) => ({ currentState: response })
  },
  {
    stage: 'communication-style',
    mystaraMessage: (ctx) => `Got it. Last thing before we begin:\n\nHow do you like to receive support? This helps me know how to show up for you.\n\nSome people want gentle encouragement. Others prefer direct feedback. Most people are somewhere in between.`,
    inputType: 'choice',
    choices: [
      { value: 'gentle', label: "Gentle — I respond best to softness and patience" },
      { value: 'direct', label: "Direct — I appreciate honest, straightforward feedback" },
      { value: 'balanced', label: "Balanced — A mix of both works for me" }
    ],
    onResponse: (response) => ({ communicationStyle: response as 'gentle' | 'direct' | 'balanced' })
  },
  {
    stage: 'closing',
    mystaraMessage: (ctx) => `Thank you, ${ctx.name}. I feel like I understand you better now.\n\nHere's what I'm taking with me:\n\n• You're seeking **${ctx.feelingUnderneath}**\n• You want to manifest: **${ctx.primaryDesire.slice(0, 80)}${ctx.primaryDesire.length > 80 ? '...' : ''}**\n• You prefer **${ctx.communicationStyle}** communication\n\nI'll remember this. Every conversation we have, I'll keep this in mind.\n\nYou're in the **Illuminate** phase — which is all about seeing yourself clearly and understanding what you truly want. No rushing, no pressure.\n\nReady to explore?`,
    inputType: 'choice',
    choices: [
      { value: 'ready', label: "I'm ready ✨" }
    ]
  }
];

export function MirrorSession({ onComplete }: MirrorSessionProps) {
  const { user } = useAuth();
  
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [context, setContext] = useState<SessionContext>({
    name: '',
    whatBroughtYou: '',
    primaryDesire: '',
    desireCategory: null,
    feelingUnderneath: '',
    currentState: '',
    communicationStyle: 'balanced'
  });
  const [inputValue, setInputValue] = useState('');
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(true);
  const [messages, setMessages] = useState<Array<{ role: 'mystara' | 'user'; content: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const currentStage = STAGES[currentStageIndex];

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Show Mystara's message with typing effect
  useEffect(() => {
    setIsTyping(true);
    const message = typeof currentStage.mystaraMessage === 'function' 
      ? currentStage.mystaraMessage(context)
      : currentStage.mystaraMessage;
    
    const timer = setTimeout(() => {
      setMessages(prev => [...prev, { role: 'mystara', content: message }]);
      setIsTyping(false);
      // Focus input after message appears
      setTimeout(() => inputRef.current?.focus(), 100);
    }, 1000 + Math.random() * 500); // Slight randomness for natural feel

    return () => clearTimeout(timer);
  }, [currentStageIndex]);

  // Save to database directly (not using hook to avoid issues)
  const saveToSoulFile = async () => {
    if (!user?.id) {
      setError('Not logged in');
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      console.log('[MirrorSession] Saving soul file for user:', user.id);

      // Update user essence
      const { error: essenceError } = await supabase
        .from('user_essence')
        .update({
          preferred_name: context.name,
          display_name: context.name,
          communication_style: context.communicationStyle
        })
        .eq('user_id', user.id);

      if (essenceError) {
        console.error('[MirrorSession] Essence error:', essenceError);
        throw essenceError;
      }

      // Add primary manifestation if provided
      if (context.primaryDesire) {
        const { error: manifestError } = await supabase
          .from('manifestations')
          .insert({
            user_id: user.id,
            title: context.primaryDesire.slice(0, 100),
            description: context.whatBroughtYou,
            category: detectCategory(context.primaryDesire, context.whatBroughtYou),
            feeling_underneath: context.feelingUnderneath,
            status: 'active'
          });

        if (manifestError) {
          console.error('[MirrorSession] Manifestation error:', manifestError);
          // Don't throw - this is optional
        }
      }

      // Mark onboarding complete - THIS IS THE CRITICAL PART
      const { error: completeError } = await supabase
        .from('soul_files')
        .update({
          onboarding_completed: true,
          mirror_session_completed_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (completeError) {
        console.error('[MirrorSession] Complete error:', completeError);
        throw completeError;
      }

      console.log('[MirrorSession] Successfully completed onboarding');
      return true;

    } catch (err: any) {
      console.error('[MirrorSession] Error saving:', err);
      setError(err.message || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Skip onboarding (for debugging or users who want to skip)
  const handleSkipOnboarding = async () => {
    if (!user?.id) return;
    
    setSaving(true);
    try {
      await supabase
        .from('soul_files')
        .update({
          onboarding_completed: true,
          mirror_session_completed_at: new Date().toISOString()
        })
        .eq('user_id', user.id);
      
      onComplete();
    } catch (err) {
      console.error('[MirrorSession] Skip error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const response = currentStage.inputType === 'choice' || currentStage.inputType === 'multi-choice'
      ? selectedChoices[0] || ''
      : inputValue;

    if (!response.trim()) return;

    // Add user message
    const displayResponse = currentStage.choices 
      ? (currentStage.choices.find((c: any) => c.value === response) as any)?.label || response
      : response;
    
    setMessages(prev => [...prev, { role: 'user', content: displayResponse }]);

    // Update context
    if (currentStage.onResponse) {
      const updates = currentStage.onResponse(response, context);
      setContext(prev => ({ ...prev, ...updates }));
    }

    // Clear input
    setInputValue('');
    setSelectedChoices([]);

    // Move to next stage or complete
    if (currentStageIndex < STAGES.length - 1) {
      setCurrentStageIndex(prev => prev + 1);
    } else {
      // Final stage - save and complete
      const success = await saveToSoulFile();
      if (success) {
        onComplete();
      }
    }
  };

  const handleChoiceSelect = async (value: string, label: string) => {
    if (currentStage.inputType === 'multi-choice') {
      setSelectedChoices(prev => 
        prev.includes(value) 
          ? prev.filter(v => v !== value)
          : [...prev, value]
      );
    } else {
      // Single choice - auto-submit
      setMessages(prev => [...prev, { role: 'user', content: label }]);
      
      if (currentStage.onResponse) {
        const updates = currentStage.onResponse(value, context);
        setContext(prev => ({ ...prev, ...updates }));
      }
      
      if (currentStageIndex < STAGES.length - 1) {
        setTimeout(() => {
          setCurrentStageIndex(prev => prev + 1);
        }, 200);
      } else {
        // Final stage
        // Need to use updated context
        const updatedContext = currentStage.onResponse 
          ? { ...context, ...currentStage.onResponse(value, context) }
          : context;
        
        setSaving(true);
        const success = await saveToSoulFile();
        if (success) {
          onComplete();
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && currentStage.inputType !== 'textarea') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-400 to-purple-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">The Mirror Session</h1>
              <p className="text-sm text-gray-500">Getting to know you</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Progress dots */}
            <div className="flex gap-1.5">
              {STAGES.map((_, i) => (
                <div 
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i < currentStageIndex 
                      ? 'bg-purple-500' 
                      : i === currentStageIndex 
                        ? 'bg-purple-300' 
                        : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            {/* Skip button */}
            <button
              onClick={handleSkipOnboarding}
              disabled={saving}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip
            </button>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="fixed top-20 left-4 right-4 z-50 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg max-w-2xl mx-auto">
          <p className="text-sm">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="pt-24 pb-40 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          <AnimatePresence>
            {messages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[85%] rounded-2xl px-5 py-4 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-pink-100 to-purple-100 text-gray-800'
                      : 'bg-white shadow-sm border border-gray-100 text-gray-700'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-white shadow-sm border border-gray-100 rounded-2xl px-5 py-4">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" />
                  <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce [animation-delay:0.15s]" />
                  <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce [animation-delay:0.3s]" />
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <AnimatePresence mode="wait">
            {!isTyping && !saving && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Choice buttons */}
                {(currentStage.inputType === 'choice' || currentStage.inputType === 'multi-choice') && currentStage.choices && (
                  <div className="flex flex-col gap-2">
                    {currentStage.choices.map((choice: any) => {
                      const value = typeof choice === 'string' ? choice : choice.value;
                      const label = typeof choice === 'string' ? choice : choice.label;
                      const isSelected = selectedChoices.includes(value);
                      
                      return (
                        <button
                          key={value}
                          onClick={() => handleChoiceSelect(value, label)}
                          className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all ${
                            isSelected
                              ? 'border-purple-400 bg-purple-50 text-purple-700'
                              : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/50 text-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{label}</span>
                            {isSelected && <Check className="w-5 h-5 text-purple-500" />}
                          </div>
                        </button>
                      );
                    })}
                    
                    {currentStage.inputType === 'multi-choice' && selectedChoices.length > 0 && (
                      <button
                        onClick={handleSubmit}
                        className="mt-2 w-full py-3 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
                      >
                        Continue
                      </button>
                    )}
                  </div>
                )}

                {/* Text input */}
                {currentStage.inputType === 'text' && (
                  <div className="flex gap-3">
                    <input
                      ref={inputRef as React.RefObject<HTMLInputElement>}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={currentStage.placeholder || 'Type your response...'}
                      className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 outline-none transition-colors"
                    />
                    <button
                      onClick={handleSubmit}
                      disabled={!inputValue.trim()}
                      className="px-6 py-4 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Textarea input */}
                {currentStage.inputType === 'textarea' && (
                  <div className="space-y-3">
                    <textarea
                      ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={currentStage.placeholder || 'Share your thoughts...'}
                      rows={3}
                      className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 outline-none transition-colors resize-none"
                    />
                    <button
                      onClick={handleSubmit}
                      disabled={!inputValue.trim()}
                      className="w-full py-3 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <span>Continue</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {saving && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-4"
              >
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-purple-500 animate-pulse" />
                <p className="text-purple-600">Saving your journey...</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

// Helper to detect manifestation category from text
function detectCategory(desire: string, context: string): ManifestationCategory {
  const combined = `${desire} ${context}`.toLowerCase();
  
  const patterns: [RegExp, ManifestationCategory][] = [
    [/career|job|work|business|professional|promotion|salary|income|entrepreneur/i, 'career'],
    [/love|relationship|partner|dating|marriage|romance|soulmate/i, 'love'],
    [/health|body|fitness|weight|energy|wellness|healing|chronic/i, 'health'],
    [/money|financial|wealth|abundance|debt|savings|rich/i, 'abundance'],
    [/creative|art|writing|music|create|express|project/i, 'creativity'],
    [/family|friend|connection|community|social|belonging/i, 'relationships'],
    [/purpose|meaning|direction|calling|mission|impact/i, 'purpose'],
  ];

  for (const [pattern, category] of patterns) {
    if (pattern.test(combined)) {
      return category;
    }
  }

  return 'growth'; // Default
}

export default MirrorSession;
